import { MIRROR_FITNESS_CARDS_ENABLED } from '@/lib/featureFlags';
import { workoutDraftKey } from '@/lib/fitnessDraft';
import { fitnessDraftWriterState } from '@/lib/fitnessDraftWriter';
import { projectFitnessLocalWorkoutRaw } from '@/lib/fitnessLocalWorkout';
import { parseFitnessDraftRestoreRaw } from '@/lib/fitnessDraftRestoreCodec';
import { getRestoreSlot, installPausedDraftCAS, getFitnessWorkoutRuntime, isFitnessWorkoutRestoreReceipt, fitnessWorkoutRestoreReceiptMatches, type FitnessWorkoutState } from '@/lib/fitnessWorkoutController';
import { canonicalRestoreArgs } from '../../supabase/functions/_shared/fitnessDraftRestoreDomain';
import { fitnessDraftRestoreRequested } from '../../supabase/functions/_shared/fitnessDraftRestoreIntent';
import { canonicalFitnessLocalWorkout, fitnessLocalWorkoutId, type FitnessLocalWorkoutEnvelope } from '../../supabase/functions/_shared/fitnessLocalWorkoutDomain';
import type { FitnessRuntimeAuth } from '@/lib/fitnessRuntimeAction';

type StorageReader = Pick<Storage, 'getItem'>;
interface BindingResponse { data: unknown; error: unknown }
interface BindingQuery { eq(column: string, value: string): BindingQuery; maybeSingle(): PromiseLike<BindingResponse> }
export interface FitnessDraftRestoreBindings { from(table: 'workout_sessions' | 'workout_plans'): { select(columns: string): BindingQuery } }
interface Origin {
  ownerId: string; auth: FitnessRuntimeAuth; bindings: FitnessDraftRestoreBindings; isCurrent: () => boolean;
  storage?: () => StorageReader; now?: () => number;
}
export interface FitnessDraftRestoreRequestOptions extends Origin { text: string }
export interface FitnessDraftRestoreOptions extends Origin { sessionId: string; actionId: string; args: unknown; request: FitnessLocalWorkoutEnvelope }
export interface PreparedFitnessDraftRestore { readonly operationId: string; readonly summary: NonNullable<FitnessLocalWorkoutEnvelope['summary']> }
export interface FitnessDraftRestoreReceipt {
  readonly version: 1; readonly owner: string; readonly sessionId: string; readonly actionId: string; readonly operationId: string;
  readonly action: 'restore_saved_draft'; readonly runtimeId: string; readonly epoch: number; readonly lifecycle: number; readonly revision: number;
  readonly phase: FitnessWorkoutState['phase']; readonly paused: true; readonly observedAt: string;
  readonly status: 'succeeded'; readonly scope: 'browser_session_runtime'; readonly evidence: 'controller_readback';
}
type Slot = ReturnType<typeof getRestoreSlot>;
interface PrivateDraft { raw: string; storage: StorageReader; slot: Slot; writerRevision: number; workout: FitnessWorkoutState }
interface Bindings { plan: { id: string; user_id: string } | null; session: { id: string; user_id: string; plan_id: string | null; completed_at: null; started_at: string; source: 'manual' | 'gym' } | null }
interface IssuedRequest { options: FitnessDraftRestoreRequestOptions; draft: PrivateDraft | null; bindings: Bindings | null }
interface PreparedPrivate { options: FitnessDraftRestoreOptions; draft: PrivateDraft; bindings: Bindings; preparedAt: number; busy: boolean; dispatched: boolean; receipt: FitnessDraftRestoreReceipt | null }
const requests = new WeakMap<FitnessLocalWorkoutEnvelope, IssuedRequest>();
const reviews = new WeakMap<PreparedFitnessDraftRestore, PreparedPrivate>();
const receipts = new WeakMap<FitnessDraftRestoreReceipt, { ownerId: string; sessionId: string; actionId: string }>();
export const FITNESS_DRAFT_RESTORE_REVIEW_TTL_MS = 2 * 60 * 1000;
const fail = (code: string): never => { throw new Error(code); };
const uuid = (value: unknown): value is string => fitnessLocalWorkoutId(value) && typeof value === 'string' && value === value.toLowerCase();
const freeze = <T,>(value: T): T => { if (value && typeof value === 'object') { Object.values(value).forEach(freeze); Object.freeze(value); } return value; };
const equal = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const nowOf = (options: Origin) => options.now?.() ?? Date.now();
function capture<T extends Origin>(input: T): T {
  return { ...input, auth: { getUser: input.auth.getUser.bind(input.auth), getSession: input.auth.getSession.bind(input.auth) }, bindings: { from: input.bindings.from.bind(input.bindings) } };
}
function assertOrigin(options: Origin): void {
  if (!uuid(options.ownerId) || !options.isCurrent()) fail('invalid_session');
  if ('sessionId' in options && (!uuid(options.sessionId) || !('actionId' in options) || !uuid(options.actionId))) fail('invalid_session');
}
function readRaw(storage: StorageReader, owner: string): string | null {
  try { return storage.getItem(workoutDraftKey(owner)); } catch { return fail('storage_unavailable'); }
}
function assertDraft(options: Origin, draft: PrivateDraft): void {
  const check = () => {
    assertOrigin(options);
    if (!equal(getRestoreSlot(options.ownerId), draft.slot)) fail('runtime_changed');
    const writer = fitnessDraftWriterState(options.ownerId);
    if (writer.active !== 0) fail('writer_active');
    if (writer.revision !== draft.writerRevision) fail('draft_changed');
  };
  check();
  if (readRaw(draft.storage, options.ownerId) !== draft.raw) fail('draft_changed');
  // Storage adapters and origin callbacks can synchronously revoke authority.
  check();
}
async function authenticate(options: Origin, guard = () => assertOrigin(options)): Promise<void> {
  guard();
  const user = await options.auth.getUser(); guard();
  if (user.error || user.data.user?.id !== options.ownerId) fail('invalid_session');
  const session = await options.auth.getSession(); guard();
  if (session.error || session.data.session?.user.id !== options.ownerId || !session.data.session.access_token) fail('invalid_session');
}
const record = (input: unknown, keys: string[]): input is Record<string, unknown> => !!input && typeof input === 'object' && !Array.isArray(input) && Object.keys(input).length === keys.length && keys.every(key => Object.prototype.hasOwnProperty.call(input, key));
async function bindingResponse(query: BindingQuery, guard: () => void): Promise<BindingResponse> {
  try { const response = await query.maybeSingle(); guard(); return response; }
  catch { guard(); return fail('binding_changed'); }
}
async function readBindings(options: Origin, draft: PrivateDraft, current = () => assertDraft(options, draft)): Promise<Bindings> {
  const guard = () => { current(); assertDraft(options, draft); }, state = draft.workout;
  guard();
  const sessionQuery = options.bindings.from('workout_sessions').select('id,user_id,plan_id,completed_at,started_at,source').eq('id', state.clientId).eq('user_id', options.ownerId);
  const session = await bindingResponse(sessionQuery, guard); guard();
  if (session.error) fail('binding_changed');
  let sessionRow: Bindings['session'] = null;
  if (session.data !== null) {
    const row: unknown = structuredClone(session.data); guard();
    if (!record(row, ['id', 'user_id', 'plan_id', 'completed_at', 'started_at', 'source'])) return fail('binding_changed');
    const startedAt = row.started_at, source = row.source;
    if (row.id !== state.clientId || row.user_id !== options.ownerId || row.plan_id !== state.planId || row.completed_at !== null || typeof startedAt !== 'string' || !Number.isFinite(Date.parse(startedAt)) || Date.parse(startedAt) !== Date.parse(state.startedAt) || source !== 'manual' && source !== 'gym') return fail('binding_changed');
    sessionRow = { id: state.clientId, user_id: options.ownerId, plan_id: state.planId, completed_at: null, started_at: new Date(startedAt).toISOString(), source };
  } else if (state.sessionId !== null) fail('binding_changed');
  let planRow: Bindings['plan'] = null;
  if (state.planId !== null) {
    const planQuery = options.bindings.from('workout_plans').select('id,user_id').eq('id', state.planId).eq('user_id', options.ownerId);
    const plan = await bindingResponse(planQuery, guard); guard();
    const row: unknown = structuredClone(plan.data); guard();
    if (plan.error || !record(row, ['id', 'user_id']) || row.id !== state.planId || row.user_id !== options.ownerId) fail('binding_changed');
    planRow = { id: state.planId, user_id: options.ownerId };
  }
  guard(); return freeze({ plan: planRow, session: sessionRow });
}

/** Reads only for the current literal request. Raw state and binding rows stay
 * private; the issued projection is retained locally across this turn's SSE. */
export async function readFitnessDraftRestoreForRequest(input: FitnessDraftRestoreRequestOptions): Promise<FitnessLocalWorkoutEnvelope | null> {
  const options = capture(input);
  if (!fitnessDraftRestoreRequested(options.text)) return null;
  assertOrigin(options);
  if (!MIRROR_FITNESS_CARDS_ENABLED) return null;
  await authenticate(options);
  let draft: PrivateDraft | null = null, bindings: Bindings | null = null;
  let status: FitnessLocalWorkoutEnvelope['status'] = 'available', summary: FitnessLocalWorkoutEnvelope['summary'] = null;
  try {
    const slot = getRestoreSlot(options.ownerId), writer = fitnessDraftWriterState(options.ownerId);
    if (writer.active) fail('writer_active');
    const source = options.storage?.() ?? window.sessionStorage;
    const storage = { getItem: source.getItem.bind(source) }, raw = readRaw(storage, options.ownerId);
    if (raw === null) status = 'absent';
    else {
      const workout = parseFitnessDraftRestoreRaw(raw, options.ownerId);
      draft = { raw, storage, slot, writerRevision: writer.revision, workout };
      assertDraft(options, draft);
      bindings = await readBindings(options, draft);
      await authenticate(options, () => assertDraft(options, draft!));
      summary = projectFitnessLocalWorkoutRaw(raw, options.ownerId, new Date(nowOf(options))).summary;
      assertDraft(options, draft);
    }
  } catch (cause) {
    if (cause instanceof Error && cause.message === 'invalid_session') throw cause;
    const code = cause instanceof Error ? cause.message : '';
    status = code === 'storage_unavailable' || code === 'writer_active' || /runtime/.test(code) ? 'unavailable' : 'invalid';
    draft = null; bindings = null;
  }
  await authenticate(options, draft ? () => assertDraft(options, draft!) : undefined);
  const now = new Date(nowOf(options));
  const envelope = freeze(canonicalFitnessLocalWorkout({ version: 1, owner: options.ownerId, observedAt: now.toISOString(), status, summary: status === 'available' ? summary : null }, options.ownerId, now));
  if (draft) assertDraft(options, draft); else assertOrigin(options);
  requests.set(envelope, { options, draft, bindings }); return envelope;
}
export function isFitnessDraftRestoreRequestCurrent(request: FitnessLocalWorkoutEnvelope): boolean {
  const issued = requests.get(request);
  if (!issued) return false;
  try { if (issued.draft) assertDraft(issued.options, issued.draft); else assertOrigin(issued.options); return true; } catch { return false; }
}

/** Review is read-only and cannot be forged by copying a network snapshot. */
export async function prepareFitnessDraftRestore(input: FitnessDraftRestoreOptions): Promise<PreparedFitnessDraftRestore> {
  const options = capture({ ...input, args: structuredClone(input.args) });
  assertOrigin(options);
  if (!MIRROR_FITNESS_CARDS_ENABLED) fail('fitness_restore_disabled');
  const args = canonicalRestoreArgs(options.args, options.ownerId, new Date(nowOf(options)));
  const issued = requests.get(options.request);
  if (!issued || issued.options.ownerId !== options.ownerId || !issued.draft || !issued.bindings || !equal(args.snapshot, options.request)) fail('review_required');
  if (!isFitnessDraftRestoreRequestCurrent(options.request)) fail('draft_changed');
  const draft = issued.draft, guard = () => { assertDraft(issued.options, draft); assertDraft(options, draft); };
  guard(); await authenticate(options, guard);
  const bindings = await readBindings(options, draft, guard); guard();
  if (!equal(bindings, issued.bindings)) fail('binding_changed');
  await authenticate(options, guard);
  const operationId = crypto.randomUUID();
  if (!uuid(operationId)) fail('invalid_operation');
  const prepared = freeze({ operationId, summary: structuredClone(args.snapshot.summary!) });
  const preparedAt = nowOf(options); guard();
  reviews.set(prepared, { options, draft, bindings, preparedAt, busy: false, dispatched: false, receipt: null }); return prepared;
}
export function fitnessDraftRestoreReceiptMatches(receipt: FitnessDraftRestoreReceipt, ownerId: string, sessionId: string, actionId?: string): boolean {
  const issued = receipts.get(receipt);
  return !!issued && issued.ownerId === ownerId && issued.sessionId === sessionId && (actionId === undefined || issued.actionId === actionId);
}
function assertAge(state: PreparedPrivate): void {
  const age = nowOf(state.options) - state.preparedAt;
  if (!Number.isFinite(age) || age < 0 || age > FITNESS_DRAFT_RESTORE_REVIEW_TTL_MS) fail('review_expired');
}
function assertRequestOrigin(options: FitnessDraftRestoreOptions): void {
  assertOrigin(options);
  const issued = requests.get(options.request);
  if (!issued || issued.options.ownerId !== options.ownerId) fail('review_required');
  assertOrigin(issued.options);
}

/** Unknown outcomes never dispatch again. A known receipt retries only fresh
 * identity and the local React acknowledgement, without a controller/DB write. */
export async function confirmFitnessDraftRestore(prepared: PreparedFitnessDraftRestore): Promise<FitnessDraftRestoreReceipt> {
  const state = reviews.get(prepared);
  if (!state) return fail('review_required');
  if (state.busy) return fail('restore_busy');
  state.busy = true;
  try {
    const { options, draft } = state;
    if (state.receipt) { await authenticate(options, () => assertRequestOrigin(options)); return state.receipt; }
    if (state.dispatched) fail('outcome_unknown');
    const guard = () => { assertAge(state); assertDraft(options, draft); const issued = requests.get(options.request); if (!issued || !issued.draft) fail('review_required'); assertDraft(issued.options, draft); };
    guard(); await authenticate(options, guard);
    const bindings = await readBindings(options, draft, guard); guard();
    if (!equal(bindings, state.bindings)) fail('binding_changed');
    await authenticate(options, guard);
    if (!MIRROR_FITNESS_CARDS_ENABLED) fail('fitness_restore_disabled');
    guard();
    // Final compare/install/readback has no await. The controller owns the
    // empty-slot CAS and acquires its writer lease before publishing the run.
    state.dispatched = true;
    try {
      const result = installPausedDraftCAS(draft.slot, draft.workout);
      const observedAt = new Date(nowOf(options)).toISOString();
      assertRequestOrigin(options);
      const actual = getFitnessWorkoutRuntime(options.ownerId);
      if (!isFitnessWorkoutRestoreReceipt(result) || !fitnessWorkoutRestoreReceiptMatches(result, actual) || !actual || !actual.paused || actual.busy !== null || actual.activeElapsedMs !== 0 || !equal(actual.state, draft.workout) || fitnessDraftWriterState(options.ownerId).active !== 1) fail('outcome_unknown');
      const receipt: FitnessDraftRestoreReceipt = freeze({ version: 1, owner: options.ownerId, sessionId: options.sessionId, actionId: options.actionId, operationId: prepared.operationId, action: 'restore_saved_draft', runtimeId: actual.runId, epoch: actual.epoch, lifecycle: actual.lifecycle, revision: actual.revision, phase: actual.state.phase, paused: true, observedAt, status: 'succeeded', scope: 'browser_session_runtime', evidence: 'controller_readback' });
      receipts.set(receipt, { ownerId: options.ownerId, sessionId: options.sessionId, actionId: options.actionId }); state.receipt = receipt; return receipt;
    } catch { return fail('outcome_unknown'); }
  } finally { state.busy = false; }
}
